import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton,
  LinearProgress, Paper, Skeleton, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tooltip, Typography
} from '@mui/material';
import {
  Close as CloseIcon, DescriptionOutlined as DocIcon, ExpandMore as ExpandIcon, MedicationOutlined as ProductIcon, Sync as RefreshIcon
} from '@mui/icons-material';
import { notify } from '../../../notifications';
import { apiBlobError, apiError, downloadBlob, plural } from '../../Documents/format';
import { fmtInt } from '../../OnlineJournal/format';
import { useLoad } from '../../OnlineJournal/hooks/useLoad';
import {
  DocumentKind, NcelsCandidate, NcelsSource, NktSource, Product1C, ProductResponse, ProductSources, Register, RelatedProduct, isCancelled,
  nomenclatureApi
} from '../nomenclatureApi';
import {
  INVALID_LABELS, NCELS_HINTS, NKT_HINTS, candidatesCommon, distinctText, fmtAmount, fmtPackQty, fmtRegDate, fmtStamp, fmtTerm, ncelsFoundBy,
  nktFoundBy, regExpired
} from '../nomenclatureCalc';
import { BORDER, EMPTY_TEXT, emptySx, headCellSx, noteSx, rowSx, smallChipSx, tabsSx } from '../nomenclatureStyles';
import { AuthImage, Field, FieldGrid, ImageRef, StatusChip } from './NomenclatureCommon';

interface Props {
  zid: string;
  onOpen: (zid: string) => void;
  onClose: () => void;
}

type CardTab = 'card' | 'register' | 'instruction' | 'related';

/** Пока позиция в очереди сверки, карточка перечитывается тихо (silent - без записи в журнал) */
const POLL_MS = 5000;
/** Во время обновления 1С сверка стоит - опрашиваем реже */
const POLL_SYNC_MS = 20000;
const MAX_POLLS = 40;

const DOC_LABELS: Record<DocumentKind, string> = { instruction: 'Инструкция', ohlp: 'ОХЛП' };

const yesNo = (v: boolean | null | undefined): string | null => (v === null || v === undefined ? null : v ? 'Да' : 'Нет');
const codeName = (v: { code: string; name: string } | null): string | null => (v ? [v.code, v.name].filter(Boolean).join(' - ') : null);
const sameText = (a: string | null, b: string | null): boolean => Boolean(a && b && a.trim().toLowerCase() === b.trim().toLowerCase());
const orEmpty = (v: React.ReactNode) => v || <Box component="span" sx={{ ...emptySx, fontWeight: 400 }}>{EMPTY_TEXT}</Box>;

/** Блок карточки: заголовок, справа - откуда данные и когда сверены */
const Section: React.FC<{ title: string; note?: React.ReactNode; children: React.ReactNode }> = ({ title, note, children }) => (
  <Paper variant="outlined" sx={{ borderColor: BORDER, borderRadius: 2, p: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'baseline', columnGap: 1.5, rowGap: 0.25, flexWrap: 'wrap', mb: 1.5 }}>
      <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#0f172a' }}>{title}</Typography>
      {note && <Typography component="div" sx={noteSx}>{note}</Typography>}
    </Box>
    {children}
  </Paper>
);

const Hint: React.FC<{ text: string }> = ({ text }) => <Typography sx={{ fontSize: '0.84rem', color: '#64748b' }}>{text}</Typography>;

const FlagChips: React.FC<{ items: Array<{ on: boolean; label: string; tip?: string }> }> = ({ items }) => {
  const active = items.filter(i => i.on);
  if (!active.length) return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.5 }}>
      {active.map(i => (
        <Tooltip key={i.label} title={i.tip || ''} disableHoverListener={!i.tip}>
          <Chip size="small" label={i.label} sx={{ ...smallChipSx, color: '#334155', bgcolor: '#f1f5f9' }} />
        </Tooltip>
      ))}
    </Box>
  );
};

/** Сверено / обновляется: «сверено 28.10.2026, 14:05 · обновляется» */
const checkedNote = (by: string | null, checkedAt: string | null | undefined, updating?: boolean): string =>
  [by, checkedAt ? `сверено ${fmtStamp(checkedAt)}` : '', updating ? 'срок перепроверки прошёл, обновляется' : ''].filter(Boolean).join(' · ');

/** Изображения товара: фото НКТ (лицевая сторона первой), если их нет - макеты упаковки из РУ */
function galleryOf(sources: ProductSources | null): Array<{ image: ImageRef; label: string }> {
  if (!sources) return [];
  const card = sources.nkt.card;
  if (card && card.images.length) return card.images.map(img => ({ image: { kind: 'nkt' as const, id: img.file }, label: img.type ? `Фото НКТ: ${img.type}` : 'Фото НКТ' }));
  const reg = sources.ncels.register;
  if (reg) return reg.boxes.map(b => ({ image: { kind: 'box' as const, id: b.id }, label: `Макет упаковки из РУ${b.name ? `: ${b.name}` : ''}` }));
  return [];
}

const Gallery: React.FC<{ items: Array<{ image: ImageRef; label: string }>; onZoom: (image: ImageRef) => void }> = ({ items, onZoom }) => {
  const [index, setIndex] = useState(0);
  const current = items[Math.min(index, items.length - 1)];
  return (
    <Box sx={{ width: { xs: '100%', sm: 260 }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <AuthImage
        image={current ? current.image : null} size="full" width="100%" height={260} alt={current ? current.label : 'Изображения нет'}
        onClick={current ? () => onZoom(current.image) : undefined}
      />
      {current && <Typography sx={{ ...noteSx, textAlign: 'center' }}>{current.label}</Typography>}
      {items.length > 1 && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {items.map((it, i) => (
            <Box
              key={`${it.image.kind}-${it.image.id}`} onClick={() => setIndex(i)}
              sx={{ cursor: 'pointer', borderRadius: 1.75, outline: i === index ? '2px solid #2563eb' : 'none', outlineOffset: 1 }}
            >
              <AuthImage image={it.image} size="thumb" width={52} height={52} alt={it.label} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const Product1CSection: React.FC<{ p: Product1C }> = ({ p }) => (
  <Section title="Данные 1С">
    <FieldGrid>
      <Field label="Артикул" value={orEmpty(p.sku)} mono />
      <Field label="Штрихкод" value={orEmpty(p.barcode)} mono />
      <Field label="Rx / OTC" value={orEmpty(p.rxotc)} />
      <Field label="Тип" value={orEmpty(p.type)} />
      <Field label="Папка" value={orEmpty(p.folder)} />
      <Field label="Категория" value={orEmpty(p.category)} />
      <Field label="Ценовая группа" value={orEmpty(p.priceGroup)} />
      <Field label="МНН" value={orEmpty(p.mnn)} />
      <Field label="Производитель" value={orEmpty(p.producer)} />
      <Field label="Делимость упаковки" value={p.partCoeff ? `1/${p.partCoeff}` : 'нет'} />
    </FieldGrid>
    <FlagChips
      items={[
        { on: p.notForMovements, label: 'Не для перемещений', tip: 'В 1С запрещено перемещать между аптеками' },
        { on: p.notForReturns, label: 'Не для возвратов', tip: 'В 1С запрещён возврат поставщику' }
      ]}
    />
  </Section>
);

const NktSection: React.FC<{ sources: ProductSources | null }> = ({ sources }) => {
  if (!sources) return <Section title="Национальный каталог товаров"><Hint text={NKT_HINTS.none} /></Section>;
  const nkt: NktSource = sources.nkt;
  if (sources.state === 'invalid') {
    const reason = sources.invalidReason ? `: ${INVALID_LABELS[sources.invalidReason]}` : '';
    return <Section title="Национальный каталог товаров"><Hint text={`${NKT_HINTS.invalid}${reason}`} /></Section>;
  }
  const card = nkt.card;
  const note = checkedNote(nktFoundBy(sources), nkt.checkedAt, nkt.updating);

  return (
    <>
      <Section title="Национальный каталог товаров" note={note}>
        {card ? (
          <>
            <FieldGrid>
              <Field label="Наименование" value={card.nameRu} wide />
              {/* поставщики часто копируют русское название во все языки - повтор не выводится */}
              <Field label="Атауы (каз.)" value={sameText(card.nameKk, card.nameRu) ? null : card.nameKk} wide />
              <Field label="Name (англ.)" value={sameText(card.nameEn, card.nameRu) ? null : card.nameEn} wide />
              <Field label="Краткое наименование" value={card.shortNameRu} />
              <Field label="GTIN" value={card.gtin} mono />
              <Field label="NTIN" value={card.ntin} mono />
              <Field label="KZTIN" value={card.kztin} mono />
              <Field label="Обязательная маркировка" value={yesNo(card.marked)} />
              <Field label="Единица измерения" value={card.measure} />
              <Field label="Количество в упаковке" value={fmtPackQty(card.packQty)} />
              <Field label="Бренд" value={card.brand} />
              <Field label="Артикул производителя" value={card.article} />
              <Field label="Производитель" value={card.producer ? card.producer.name : null} />
              <Field label="Страна производства" value={card.country ? card.country.name : null} />
              <Field label="Товар ЕАЭС" value={yesNo(card.eaes)} />
              <Field label="Социально значимый (СЗПТ)" value={yesNo(card.szpt)} />
              <Field label="ТН ВЭД" value={codeName(card.tnved)} wide />
              <Field label="Категория ОКТРУ" value={card.oktru && card.oktru.length ? card.oktru.map(n => n.name).join(' → ') : null} wide />
              {card.extra.map(a => <Field key={a.name} label={a.name} value={a.value} />)}
            </FieldGrid>
            <Typography sx={{ ...noteSx, mt: 1.5 }}>
              {[
                card.cards > 1 ? `В НКТ ${fmtInt(card.cards)} ${plural(card.cards, 'карточка', 'карточки', 'карточек')} с этим штрихкодом, показана основная` : '',
                card.updatedAt ? `карточка изменена в НКТ ${fmtStamp(card.updatedAt)}` : ''
              ].filter(Boolean).join(' · ')}
            </Typography>
          </>
        ) : (
          <Hint text={nkt.status === 'ofd' ? 'Карточки в НКТ нет - ниже сведения ОФД' : NKT_HINTS[nkt.status]} />
        )}
        {nkt.error && nkt.status === 'error' && <Typography sx={{ ...noteSx, color: '#b91c1c', mt: 1 }}>{nkt.error}</Typography>}
      </Section>

      {nkt.ofd && (
        <Section title="ОФД (nct.gov.kz)" note={nkt.ofdCheckedAt ? `сверено ${fmtStamp(nkt.ofdCheckedAt)}` : undefined}>
          <FieldGrid>
            <Field label="Наименование" value={nkt.ofd.nameRu} wide />
            <Field label="NTIN" value={nkt.ofd.ntin} mono />
            <Field label="Обязательная маркировка" value={yesNo(nkt.ofd.marked)} />
            <Field label="Статус в ОФД" value={nkt.ofd.status} />
          </FieldGrid>
        </Section>
      )}
    </>
  );
};

const SubstancesTable: React.FC<{ reg: Register }> = ({ reg }) => (
  <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, mt: 1.5 }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={headCellSx}>Вещество</TableCell>
          <TableCell sx={{ ...headCellSx, width: 200 }}>Вид</TableCell>
          <TableCell sx={{ ...headCellSx, width: 160 }} align="right">Количество</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {reg.substances.map((s, i) => (
          <TableRow key={`${s.name}-${i}`} sx={rowSx}>
            <TableCell sx={{ fontWeight: 600, color: '#0f172a' }}>{s.name}</TableCell>
            <TableCell sx={{ color: '#64748b' }}>{s.type || '-'}</TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtAmount(s.amount, s.measure) || '-'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const RegisterView: React.FC<{ reg: Register; onZoom: (image: ImageRef) => void }> = ({ reg, onZoom }) => {
  const expired = !reg.unlimited && regExpired(reg.expirationDate);
  const until = reg.unlimited ? 'бессрочно' : fmtRegDate(reg.expirationDate);
  const recipe = reg.recipe === null ? null : reg.recipe ? 'По рецепту' : 'Без рецепта';
  const forms = reg.forms
    .map(f => distinctText([f.dosage, f.concentration, f.packing, f.units ? `№${f.units}` : '']).join(', '))
    .filter(Boolean);

  return (
    <>
      <FieldGrid>
        <Field label="Торговое название" value={reg.tradeName} />
        <Field label="МНН" value={reg.inn} />
        <Field label="Номер РУ" value={[reg.regNumber, reg.regType].filter(Boolean).join(' · ') || null} mono />
        <Field label="Дата регистрации" value={fmtRegDate(reg.regDate)} />
        <Field
          label="Действует до"
          value={until && <Box component="span" sx={{ color: expired ? '#b91c1c' : undefined }}>{until}{expired ? ' (срок истёк)' : ''}</Box>}
        />
        <Field label="Вид регистрации" value={reg.regAction} />
        <Field label="АТХ" value={codeName(reg.atc)} />
        <Field label="Отпуск" value={distinctText([recipe, reg.recipeText]).join(' - ') || null} />
        <Field label="Вид препарата" value={reg.drugType} />
        <Field label="Лекарственная форма" value={reg.dosageForm} />
        <Field label="Дозировка" value={reg.concentration} />
        <Field label="Уточнение дозировки" value={reg.dosageComment} />
        <Field label="Производитель" value={[reg.producer, reg.producerEn].filter(Boolean).join(' / ') || null} />
        <Field label="Страна производителя" value={reg.country} />
        <Field label="Нормативный документ" value={reg.nd ? [reg.nd.name, reg.nd.number].filter(Boolean).join(' № ') : null} />
        <Field label="Способ применения" value={reg.useMethod} />
        <Field label="Срок хранения" value={fmtTerm(reg.storage)} />
        <Field label="Дополнительно" value={reg.additionalFeature} wide />
        <Field label="Формы выпуска" value={forms.length ? forms.join('; ') : null} wide />
      </FieldGrid>
      <FlagChips
        items={[
          { on: reg.gmp, label: 'GMP', tip: 'Производство сертифицировано по GMP' },
          { on: reg.generic, label: 'Генерик' },
          { on: reg.biosimilar, label: 'Биоаналог' },
          { on: reg.child, label: 'Детская форма' },
          { on: reg.sugar, label: 'Содержит сахар' }
        ]}
      />
      {reg.substances.length > 0 && <SubstancesTable reg={reg} />}
      {reg.boxes.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569', mb: 1 }}>Упаковки и макеты</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
            {reg.boxes.map(b => {
              const size = distinctText([b.count ? `${b.count} шт.` : '', b.volume ? fmtAmount(b.volume, b.measure) : '', b.units && String(b.units) !== String(b.count) ? `№${b.units}` : '']).join(' · ');
              return (
                <Box key={b.id} sx={{ display: 'flex', gap: 1.25, p: 1, border: `1px solid ${BORDER}`, borderRadius: 2, minWidth: 0 }}>
                  <AuthImage
                    image={{ kind: 'box', id: b.id }} size="thumb" width={72} height={72} alt={b.name || 'Макет упаковки'}
                    onClick={() => onZoom({ kind: 'box', id: b.id })}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#0f172a' }}>{b.name || 'Упаковка'}</Typography>
                    {(b.type || size) && <Typography sx={{ fontSize: '0.76rem', color: '#64748b' }}>{[b.type, size].filter(Boolean).join(' · ')}</Typography>}
                    {b.description && <Typography sx={{ fontSize: '0.74rem', color: '#94a3b8', mt: 0.25 }}>{b.description}</Typography>}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
};

const CandidatesView: React.FC<{ list: NcelsCandidate[] }> = ({ list }) => {
  const common = candidatesCommon(list);
  return (
    <>
      <Alert severity="info" sx={{ py: 0.25, mb: 1.5 }}>
        Под название подходит {fmtInt(list.length)} {plural(list.length, 'удостоверение', 'удостоверения', 'удостоверений')} - в карточке
        только общие для них сведения. Точное РУ определится, когда в НКТ или 1С появится число в упаковке или дозировка.
      </Alert>
      {common.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <FieldGrid>{common.map(c => <Field key={c.label} label={c.label} value={c.value} />)}</FieldGrid>
        </Box>
      )}
      <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: 2 }}>
        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: 150 }}>Номер РУ</TableCell>
              <TableCell sx={headCellSx}>Торговое название / форма</TableCell>
              <TableCell sx={{ ...headCellSx, width: 220 }}>Производитель</TableCell>
              <TableCell sx={{ ...headCellSx, width: 110 }}>Действует до</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map(c => (
              <TableRow key={c.id} sx={rowSx}>
                <TableCell sx={{ fontFamily: 'monospace' }}>{c.regNumber || '-'}</TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{c.tradeName || '-'}</Typography>
                  {c.form && <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>{c.form}</Typography>}
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: 13 }}>{c.producer || '-'}</Typography>
                  {c.country && <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>{c.country}</Typography>}
                </TableCell>
                <TableCell sx={{ color: regExpired(c.expirationDate) ? '#b91c1c' : undefined }}>{fmtRegDate(c.expirationDate) || 'бессрочно'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

const RegisterSection: React.FC<{ ncels: NcelsSource; onZoom: (image: ImageRef) => void }> = ({ ncels, onZoom }) => {
  const note = checkedNote(ncelsFoundBy(ncels), ncels.checkedAt, ncels.updating);
  let body: React.ReactNode;
  if (ncels.status === 'found' && ncels.register) body = <RegisterView reg={ncels.register} onZoom={onZoom} />;
  else if (ncels.status === 'multiple' && ncels.candidates && ncels.candidates.length) body = <CandidatesView list={ncels.candidates} />;
  else body = <Hint text={NCELS_HINTS[ncels.status]} />;
  return (
    <Section title="Госреестр лекарственных средств (НЦЭЛС)" note={note}>
      {body}
      {ncels.error && ncels.status === 'error' && <Typography sx={{ ...noteSx, color: '#b91c1c', mt: 1 }}>{ncels.error}</Typography>}
    </Section>
  );
};

/** Разделы инструкции из госреестра и файлы инструкции / ОХЛП */
const InstructionSection: React.FC<{ reg: Register }> = ({ reg }) => {
  const [downloading, setDownloading] = useState<DocumentKind | null>(null);
  const download = async (kind: DocumentKind) => {
    setDownloading(kind);
    try {
      const file = await nomenclatureApi.document(reg.id, kind);
      downloadBlob(file.filename, file.blob);
    } catch (err) {
      if (!isCancelled(err)) notify.error(await apiBlobError(err, `Не удалось скачать: ${DOC_LABELS[kind]}`));
    } finally {
      setDownloading(null);
    }
  };
  const sections = reg.instruction || [];
  // файла инструкции может не быть; наличие ОХЛП источник заранее не сообщает - при отсутствии придёт 404
  const kinds: DocumentKind[] = reg.instructionKind === 'none' ? ['ohlp'] : ['instruction', 'ohlp'];

  return (
    <Section title="Инструкция по применению" note={reg.regNumber ? `по РУ ${reg.regNumber}` : undefined}>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: sections.length ? 1.5 : 0 }}>
        {kinds.map(kind => (
          <Tooltip key={kind} title={kind === 'ohlp' ? 'Общая характеристика лекарственного препарата - для специалистов' : `Файл из госреестра (${reg.instructionKind.toUpperCase()})`}>
            <span>
              <Button
                size="small" variant="outlined" onClick={() => download(kind)} disabled={downloading !== null}
                startIcon={downloading === kind ? <CircularProgress size={14} color="inherit" /> : <DocIcon />}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
              >
                {DOC_LABELS[kind]}
              </Button>
            </span>
          </Tooltip>
        ))}
      </Box>
      {sections.length ? sections.map((s, i) => (
        <Accordion
          key={`${s.title}-${i}`} disableGutters elevation={0} defaultExpanded={i === 0}
          sx={{ border: `1px solid ${BORDER}`, borderRadius: '8px !important', mb: 0.75, '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
            <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: '#0f172a' }}>{s.title}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {s.text.split('\n').filter(line => line.trim()).map((line, j) => (
              <Typography key={j} sx={{ fontSize: '0.84rem', color: '#334155', lineHeight: 1.55, mb: 0.75 }}>{line}</Typography>
            ))}
          </AccordionDetails>
        </Accordion>
      )) : <Hint text="Текст инструкции в госреестре не разобран - скачайте файл" />}
    </Section>
  );
};

const RelatedList: React.FC<{ title: string; rows: RelatedProduct[]; note?: string; empty: string; onOpen: (zid: string) => void }> = ({
  title, rows, note, empty, onOpen
}) => (
  <Section title={`${title} (${fmtInt(rows.length)})`} note={note}>
    {rows.length ? (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {rows.map(r => (
          <Box
            key={r.zid} onClick={() => onOpen(r.zid)}
            sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, py: 0.75, px: 1, borderRadius: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
          >
            <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: '#1d4ed8', flexGrow: 1, minWidth: 0 }}>{r.name}</Typography>
            <Typography sx={{ fontSize: '0.76rem', color: '#64748b', flexShrink: 0, maxWidth: '45%', textAlign: 'right' }}>
              {[r.producer, r.type].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        ))}
      </Box>
    ) : <Hint text={empty} />}
  </Section>
);

/** Карточка товара: 1С, Национальный каталог (с ОФД), госреестр НЦЭЛС, инструкция, товары с тем же штрихкодом и МНН */
const ProductDialog: React.FC<Props> = ({ zid, onOpen, onClose }) => {
  const [tab, setTab] = useState<CardTab>('card');
  const [pollToken, setPollToken] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [zoom, setZoom] = useState<ImageRef | null>(null);
  const polls = useRef(0);

  const card = useLoad(
    (silent: boolean, signal: AbortSignal) => nomenclatureApi.product(zid, silent, signal),
    zid, pollToken, 'Не удалось загрузить карточку товара'
  );
  // ответ по прошлому товару (переход по связанным) не показываем
  const data: ProductResponse | null = card.stale ? null : card.data;

  // другой товар - с первой вкладки и с новым лимитом опросов
  useEffect(() => {
    setTab('card');
    polls.current = 0;
  }, [zid]);

  const pending = Boolean(data && data.pending);
  const waitingSync = Boolean(data && data.waitingSync);
  useEffect(() => {
    if (!pending || card.loading || polls.current >= MAX_POLLS) return undefined;
    const timer = window.setTimeout(() => {
      polls.current += 1;
      setPollToken(t => t + 1);
    }, waitingSync ? POLL_SYNC_MS : POLL_MS);
    return () => window.clearTimeout(timer);
  }, [pending, waitingSync, card.loading, data]);

  const gallery = useMemo(() => galleryOf(data ? data.sources : null), [data]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const res = await nomenclatureApi.refresh(zid);
      if (res.waitingSync) notify.warning(res.message);
      else notify.success(res.message);
      polls.current = 0;
      setPollToken(t => t + 1);
    } catch (err) {
      notify.error(apiError(err, 'Не удалось поставить позицию на сверку'));
    } finally {
      setRefreshing(false);
    }
  };

  const sources = data ? data.sources : null;
  const reg = sources && sources.ncels.status === 'found' ? sources.ncels.register || null : null;
  const hasInstruction = Boolean(reg);
  const relatedCount = data ? data.sameBarcode.length + data.analogs.length : 0;
  const canRefresh = Boolean(data && data.permissions.refresh && sources && sources.state === 'ok');
  const activeTab: CardTab = (tab === 'instruction' && !hasInstruction) || (tab === 'register' && !sources) ? 'card' : tab;
  const p = data ? data.product : null;
  const caption = p ? [p.sku ? `арт. ${p.sku}` : '', p.barcode ? `ШК ${p.barcode}` : '', p.type || ''].filter(Boolean).join(' · ') : '';

  return (
    <Dialog
      open onClose={onClose} maxWidth={false}
      // высота постоянная: при смене вкладки окно не прыгает и кнопки шапки остаются на месте
      PaperProps={{ sx: { width: '100%', maxWidth: 1240, height: 'calc(100% - 64px)', borderRadius: 3, boxShadow: '0 20px 48px rgba(15, 23, 42, 0.16)', overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ py: 2, px: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#fff', borderBottom: `1px solid ${BORDER}` }}>
        <Box sx={{ width: 44, height: 44, flexShrink: 0, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb', bgcolor: 'rgba(37,99,235,0.08)' }}>
          <ProductIcon />
        </Box>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          {p ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>{p.name}</Typography>
                {sources && <StatusChip source="nkt" status={sources.nkt.status} hint={sources.invalidReason ? INVALID_LABELS[sources.invalidReason] : undefined} />}
                {sources && <StatusChip source="ncels" status={sources.ncels.status} />}
              </Box>
              {caption && <Typography sx={{ fontSize: '0.78rem', color: '#64748b', mt: 0.25, fontFamily: 'monospace' }} noWrap title={caption}>{caption}</Typography>}
            </>
          ) : <Skeleton width={360} height={28} />}
        </Box>
        {canRefresh && (
          <Tooltip title="Поставить позицию в начало очереди и заново сверить с НКТ и госреестром">
            <span>
              <Button
                size="small" onClick={refresh} disabled={refreshing}
                startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
                sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
              >
                Сверить сейчас
              </Button>
            </span>
          </Tooltip>
        )}
        <IconButton onClick={onClose} size="small" aria-label="Закрыть" sx={{ color: 'text.secondary', alignSelf: 'flex-start' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box sx={{ height: 3 }}>{card.loading && data && <LinearProgress sx={{ height: 3 }} />}</Box>

      <DialogContent sx={{ px: 2.5, pb: 2.5, pt: '4px !important', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {card.error && (
          <Alert
            severity={card.error.forbidden ? 'warning' : 'error'}
            action={card.error.forbidden ? undefined : <Button color="inherit" size="small" onClick={card.retry}>Повторить</Button>}
          >
            {card.error.text}
          </Alert>
        )}

        {!data && !card.error && <Skeleton variant="rounded" height={360} sx={{ borderRadius: 2, mt: 1 }} />}

        {data && p && (
          <>
            {pending && (
              <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ py: 0.25 }}>
                {waitingSync
                  ? 'Идёт обновление данных 1С - позиция сверится с источниками сразу после него, карточка обновится сама.'
                  : 'Позиция сверяется с Национальным каталогом и госреестром - карточка обновится сама.'}
                {polls.current >= MAX_POLLS && ' Автообновление остановлено - откройте карточку позже.'}
              </Alert>
            )}

            <Box sx={{ borderBottom: `1px solid ${BORDER}` }}>
              <Tabs value={activeTab} onChange={(_, v: CardTab) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={tabsSx}>
                <Tab value="card" label="Карточка" />
                {sources && <Tab value="register" label="Госреестр" />}
                {hasInstruction && <Tab value="instruction" label="Инструкция" />}
                <Tab value="related" label={`Связанные (${fmtInt(relatedCount)})`} />
              </Tabs>
            </Box>

            {activeTab === 'card' && (
              <>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'flex-start' }}>
                  <Gallery key={zid} items={gallery} onZoom={setZoom} />
                  <Box sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}><Product1CSection p={p} /></Box>
                </Box>
                <NktSection sources={sources} />
              </>
            )}

            {activeTab === 'register' && sources && <RegisterSection ncels={sources.ncels} onZoom={setZoom} />}

            {activeTab === 'instruction' && reg && <InstructionSection reg={reg} />}

            {activeTab === 'related' && (
              <>
                <RelatedList
                  title="С тем же штрихкодом" rows={data.sameBarcode} onOpen={onOpen}
                  empty={p.barcode ? 'Других товаров с этим штрихкодом в 1С нет' : 'У товара нет штрихкода'}
                />
                <RelatedList
                  title="Аналоги по МНН" rows={data.analogs} onOpen={onOpen}
                  note={data.analogsTruncated ? 'показаны не все - уточните поиск по МНН в списке' : undefined}
                  empty={p.mnn ? 'Других товаров с этим МНН в 1С нет' : 'В 1С у товара не указан МНН'}
                />
              </>
            )}
          </>
        )}
      </DialogContent>

      <Dialog open={zoom !== null} onClose={() => setZoom(null)} maxWidth="md" fullWidth>
        <Box sx={{ position: 'relative', p: 1.5, bgcolor: '#fff' }}>
          <IconButton onClick={() => setZoom(null)} size="small" aria-label="Закрыть" sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: '#fff' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
          {zoom && <AuthImage image={zoom} size="full" width="100%" height="75vh" alt={p ? p.name : 'Изображение'} />}
        </Box>
      </Dialog>
    </Dialog>
  );
};

export default ProductDialog;
